/* Alerts · what the fleet flagged. Design: ConsoleAlerts.dc.html.
   Urgent (2026-10-28, Gabriel) is the classic dashboard's serious queue, from
   data/urgent (Overview counts the same list): the urgent alerts, every fuel
   alert, the buses that are off the road (suspended or in maintenance, with who
   took them off and when, from busStatusEvents), papers and services past due,
   buses burning well over their normal, and the fuel set-up gaps. Incident
   alerts are not here: each one is a driver's report, listed and closed in
   Analytics → Incidents. ?tab=urgent opens that tab (Overview links to it). */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useFleet } from '../data/FleetData';
import type { Bus, FleetAlert, Severity } from '../data/model';
import { openIncidents } from '../data/analytics';
import { isFuelKind, thirstPct, urgentOf } from '../data/urgent';
import { EditBusModal } from '../BusModals';
import { MaintFocus, MaintModal } from '../MaintModal';
import { RefillModal } from '../RefillModal';
import { alertInvitesRefuelCorrection } from '../../services/refuelCorrection';
import { usePage, useShell } from '../Shell';
import { Empty, PageBody, PageSkeleton, StaleBanner, ago, dayMonth, hhmm, n0, plural, useAction, useGo } from '../kit';
import { platePlain } from '../data/zana';

interface StatusEvent { busId: string; toStatus: string; at: Date | null; by: string | null }

/** The newest status change per bus — who took it off the road, and when. */
function useStatusEvents(orgCode: string) {
  const [latest, setLatest] = useState<Map<string, StatusEvent>>(new Map());
  useEffect(() => {
    if (!orgCode) return undefined;
    // No orderBy: that would need a composite index. A school changes status rarely.
    return onSnapshot(query(collection(db, 'busStatusEvents'), where('orgCode', '==', orgCode), limit(200)), (snap) => {
      const m = new Map<string, StatusEvent>();
      snap.docs.forEach((d) => {
        const x = d.data() as { busId?: string; toStatus?: string; at?: { toDate?: () => Date }; byEmail?: string | null };
        const ev = { busId: String(x.busId || ''), toStatus: String(x.toStatus || ''), at: x.at?.toDate?.() ?? null, by: x.byEmail || null };
        const had = m.get(ev.busId);
        if (!had || (ev.at?.getTime() ?? 0) > (had.at?.getTime() ?? 0)) m.set(ev.busId, ev);
      });
      setLatest(m);
    }, () => setLatest(new Map()));
  }, [orgCode]);
  return latest;
}

type Tab = 'new' | 'all' | 'urgent';
const SHOW_MAX = 200;

const SEV: Record<Severity, { cls: string; label: string }> = {
  urgent: { cls: 'c-bad', label: 'Urgent' },
  check: { cls: 'c-warn', label: 'Check' },
  info: { cls: 'c-mute', label: 'Info' },
};

/** "Today 06:41" / "Yesterday 17:20" / "Mon 20 Oct", as the design writes it. */
function when(d: Date | null): string {
  if (!d) return '—';
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return `Today ${hhmm(d)}`;
  return ago(d);
}

/* Alert types that are about where a bus is, and those that are about fuel
   (the vocabulary in src/fleet/alertMeta.ts). */
const MAP_KINDS = new Set([
  'incident', 'traffic', 'bus_route_timeout', 'bus_stationary', 'bus_signal_lost', 'gps_discrepancy',
  'route_ended_with_students_aboard',
]);

const Alerts: React.FC = () => {
  const f = useFleet();
  const go = useGo();
  const action = useAction();
  const { ask } = useShell();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const t = params.get('tab');
    return t === 'urgent' || t === 'all' ? t : 'new';
  });
  const events = useStatusEvents(f.org.code);

  const shownAlerts = f.alerts.filter((a) => a.kind !== 'incident');
  const unread = shownAlerts.filter((a) => !a.read);
  const u = urgentOf(f);
  const { alerts: urgent, offRoad, gaps } = u;
  /** Panels the Urgent tab shows above its alerts. */
  const others = u.count - urgent.length;
  const [editing, setEditing] = useState<{ bus: Bus; focus?: 'odometer' } | null>(null);
  const [correcting, setCorrecting] = useState<{ bus: Bus; alert: FleetAlert } | null>(null);
  const [maint, setMaint] = useState<{ mode: 'log' | 'edit'; busId: string; focus?: MaintFocus } | null>(null);
  const incidents = openIncidents(f).length;
  const flagged = unread.find((a) => a.busPlate);

  usePage({
    key: 'ConsoleAlerts', title: 'Alerts', zanaContext: `Alerts · ${unread.length} new`,
    zanaIntro: 'I can sort alerts into what needs you and what does not.',
    zana: {
      cards: [{ label: 'Unread alerts', value: String(unread.length) }, { label: 'Urgent', value: String(u.count) }],
      focus: [...new Set(unread.map((a) => a.busPlate).filter((p): p is string => !!p).map(platePlain))],
    },
    prompts: ['Which alerts can I ignore?', ...(flagged ? [`Why was ${flagged.busPlate} flagged?`] : ['What did the fleet flag this week?'])],
  });

  const list = tab === 'new' ? unread : tab === 'urgent' ? urgent : shownAlerts;
  const shown = list.slice(0, SHOW_MAX);

  const markAll = () => {
    const n = unread.length;
    if (!n) return;
    void action({
      busy: `Marking ${plural(n, 'alert')} read…`,
      done: `${plural(n, 'alert')} marked read`,
      fail: 'Couldn’t mark the alerts read. Try again in a minute.',
      run: () => f.markAllAlertsRead(),
    });
  };
  const markOne = (a: FleetAlert) => {
    void action({
      busy: 'Marking read…',
      done: 'Alert marked read',
      fail: 'Couldn’t mark that alert read. Try again in a minute.',
      run: () => f.markAlertRead(a.id),
    });
  };

  /** The one place to go next for this alert. */
  const rowLink = (a: FleetAlert): React.ReactNode => {
    const busQ = a.busId ? `?bus=${encodeURIComponent(a.busId)}` : '';
    // A refill whose figures disagree with something: the office can correct it here.
    const flaggedBus = alertInvitesRefuelCorrection(a.kind) && a.busId ? f.buses.find((b) => b.id === a.busId) : undefined;
    if (flaggedBus) return <button type="button" className="btn s b-line" onClick={() => setCorrecting({ bus: flaggedBus, alert: a })}>Correct the refill</button>;
    if (isFuelKind(a.kind)) return <a className="btn s b-line" {...go('ConsoleFuel')}>Check refill</a>;
    if (MAP_KINDS.has(a.kind)) return <a className="btn s b-line" {...go('ConsoleLiveMap', busQ)}>See on map</a>;
    if (a.busId) return <a className="btn s b-line" {...go('ConsoleBuses', busQ)}>See bus</a>;
    return null;
  };

  const sub = unread.length === 0
    ? 'Nothing new. Alerts appear here as soon as the fleet flags something.'
    : `${unread.length} new.${u.count ? ` ${u.count} urgent.` : ''}`;

  const offRow = (b: Bus) => {
    const why = String(b.raw.status || '') === 'suspended' ? 'Suspended' : 'In maintenance';
    const ev = events.get(b.id);
    return (
      <div key={b.id} className="todo">
        <span className="chip c-bad" style={{ width: '104px', justifyContent: 'center', flex: 'none' }}>Off the road</span>
        <div style={{ flex: '1', minWidth: '0', display: 'flex', flexDirection: 'column' }}>
          <b>{why} · {b.name}</b>
          <span className="sm" style={{ color: 'var(--muted)', fontSize: '14px' }}>
            {b.plate}{ev && ev.toStatus !== 'active' && ev.at ? ` · since ${dayMonth(ev.at)}${ev.by ? ` · by ${ev.by}` : ''}` : ''}{b.expected ? ` · ${plural(b.expected, 'child', 'children')} on its list` : ''}
          </span>
        </div>
        <a className="btn s b-line" {...go('ConsoleBuses', `?bus=${encodeURIComponent(b.id)}`)}>See bus</a>
      </div>
    );
  };

  return (
    <PageBody loading={f.loading} skeleton={<PageSkeleton stats />}>
      <div className={'wrap' + (f.error ? ' stale' : '')} style={{ maxWidth: '1040px' }}>
        {f.error && <StaleBanner what="alerts" since={f.updatedAt} onRetry={f.retry} />}
        <div className="phead">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <h1 className="h1">Alerts</h1>
            <p className="sub">{sub}</p>
          </div>
          <button type="button" className="btn b-line" onClick={markAll} disabled={unread.length === 0}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
              <path d="m5 12 5 5 9-10" />
            </svg>
            Mark all read
          </button>
        </div>
        <div className="seg" role="tablist" aria-label="Which alerts">
          <button type="button" role="tab" aria-selected={tab === 'new'} className={tab === 'new' ? 'on' : ''} onClick={() => setTab('new')}>New {unread.length}</button>
          <button type="button" role="tab" aria-selected={tab === 'all'} className={tab === 'all' ? 'on' : ''} onClick={() => setTab('all')}>All</button>
          <button type="button" role="tab" aria-selected={tab === 'urgent'} className={tab === 'urgent' ? 'on' : ''} onClick={() => setTab('urgent')}>Urgent {u.count}</button>
        </div>
        {incidents > 0 && (
          <div className="banner warn" role="status">
            <span style={{ flex: 1 }}><b>{plural(incidents, 'incident is', 'incidents are')} open.</b> Drivers’ incident reports are in Analytics, where you close them.</span>
            <a className="btn s b-line" {...go('ConsoleAnalytics', '?tab=incidents')}>See incidents</a>
          </div>
        )}
        {tab === 'urgent' && offRoad.length > 0 && (
          <section className="panel" style={{ overflow: 'hidden' }}>
            <div className="ph">
              <h2 className="h2">Off the road</h2>
              <span className="sm" style={{ color: 'var(--muted)' }}>{plural(offRoad.length, 'bus', 'buses')} suspended or in maintenance</span>
            </div>
            {offRoad.map(offRow)}
          </section>
        )}
        {tab === 'urgent' && u.pastDue.length > 0 && (
          <section className="panel" style={{ overflow: 'hidden' }}>
            <div className="ph">
              <h2 className="h2">Papers and service</h2>
              <span className="sm" style={{ color: 'var(--muted)' }}>Past due on buses still on the road</span>
            </div>
            {u.pastDue.map(({ bus, papers, service }) => (
              <div key={bus.id} className="todo">
                <span className={`chip ${papers.length ? 'c-bad' : 'c-warn'}`} style={{ width: '104px', justifyContent: 'center', flex: 'none' }}>{papers.length ? 'Not legal' : 'Service'}</span>
                <div style={{ flex: '1', minWidth: '0', display: 'flex', flexDirection: 'column' }}>
                  <b>
                    {papers.length
                      ? `${papers.map((p) => p.label).join(' and ')} expired`
                      : 'Service overdue'} · {bus.name}
                  </b>
                  <span className="sm" style={{ color: 'var(--muted)', fontSize: '14px' }}>
                    {papers.length ? `Since ${dayMonth(papers.reduce((a, p) => (p.due < a ? p.due : a), papers[0].due))}. It is not legal to run until renewed.` : ''}
                    {papers.length && service ? ' ' : ''}
                    {service ? `${papers.length ? 'Its service is overdue too' : service.label}${service.kmOver != null ? ` · ${n0(service.kmOver)} km over` : service.due ? ` · due ${dayMonth(service.due)}` : ''}.` : ''}
                  </span>
                </div>
                {papers.length
                  ? <button type="button" className="btn s b-ink" onClick={() => setMaint({ mode: 'edit', busId: bus.id, focus: papers.some((p) => /insur/i.test(p.label)) ? 'insurance' : 'inspection' })}>Update dates</button>
                  : <button type="button" className="btn s b-line" onClick={() => setMaint({ mode: 'log', busId: bus.id })}>Log service</button>}
              </div>
            ))}
          </section>
        )}
        {tab === 'urgent' && u.thirsty.length > 0 && (
          <section className="panel" style={{ overflow: 'hidden' }}>
            <div className="ph">
              <h2 className="h2">Fuel use</h2>
              <span className="sm" style={{ color: 'var(--muted)' }}>A quarter or more above each bus’s own normal, last 30 days</span>
            </div>
            {u.thirsty.map((b) => (
              <div key={b.id} className="todo">
                <span className="chip c-warn" style={{ width: '104px', justifyContent: 'center', flex: 'none' }}>Fuel</span>
                <div style={{ flex: '1', minWidth: '0', display: 'flex', flexDirection: 'column' }}>
                  <b>{b.plate} is using {thirstPct(b)}% more fuel than its normal</b>
                  <span className="sm" style={{ color: 'var(--muted)', fontSize: '14px' }}>
                    {b.kmpl?.toFixed(1)} km per litre against its own {b.kmplNormal?.toFixed(1)}.
                  </span>
                </div>
                <button type="button" className="btn s b-ghost" onClick={() => ask(`Why is ${b.plate} burning more fuel?`)}>Why?</button>
                <a className="btn s b-line" {...go('ConsoleFuel')}>See fuel</a>
              </div>
            ))}
          </section>
        )}
        {tab === 'urgent' && gaps.length > 0 && (
          <section className="panel" style={{ overflow: 'hidden' }}>
            <div className="ph">
              <h2 className="h2">Fuel set-up</h2>
              <span className="sm" style={{ color: 'var(--muted)' }}>Each clears itself once it is fixed</span>
            </div>
            {gaps.map((g) => (
              <div key={`${g.kind}-${g.bus.id}`} className="todo">
                <span className="chip c-warn" style={{ width: '104px', justifyContent: 'center', flex: 'none' }}>Fuel</span>
                <div style={{ flex: '1', minWidth: '0', display: 'flex', flexDirection: 'column' }}>
                  <b>{g.kind === 'tank' ? 'No tank size' : 'No odometer reading yet'} · {g.bus.name}</b>
                  <span className="sm" style={{ color: 'var(--muted)', fontSize: '14px' }}>
                    {g.kind === 'tank'
                      ? 'Litres, cost and fuel-loss warnings for this bus are guessed until its real tank size is set.'
                      : 'Km per litre needs two odometer readings. Set today’s reading here, or the driver is asked at the next trip start.'}
                  </span>
                </div>
                {g.kind === 'tank'
                  ? <button type="button" className="btn s b-ink" onClick={() => setEditing({ bus: g.bus })}>Set tank size</button>
                  : <button type="button" className="btn s b-line" onClick={() => setEditing({ bus: g.bus, focus: 'odometer' })}>Set reading</button>}
              </div>
            ))}
          </section>
        )}
        {!(shown.length === 0 && tab === 'urgent' && others > 0) && (
        <section className="panel" style={{ overflow: 'hidden' }} data-z="alerts">
          {tab === 'urgent' && others > 0 && <div className="ph"><h2 className="h2">Alerts</h2></div>}
          {shown.length === 0 && (
            tab === 'all' ? (
              <Empty title="No alerts yet" body="Late buses, fuel that looks wrong and lost GPS signals appear here as soon as they happen." />
            ) : (
              <Empty
                icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12 5 5 9-10" /></svg>}
                title={tab === 'urgent' ? 'Nothing urgent' : 'Nothing new'}
                body={tab === 'urgent' ? 'No unread alert needs you right now. Children still aboard, fuel that looks wrong and buses taken off the road show here first.' : 'You have read every alert. New ones appear here as soon as the fleet flags something.'}
              >
                {shownAlerts.length > 0 && <button type="button" className="btn s b-line" onClick={() => setTab('all')}>See all alerts</button>}
              </Empty>
            )
          )}
          {shown.map((a) => (
            <div key={a.id} className="todo" style={a.read ? { opacity: 0.72 } : undefined}>
              <span className={`chip ${SEV[a.severity].cls}`} style={{ width: '104px', justifyContent: 'center', flex: 'none' }}>
                {SEV[a.severity].label}
              </span>
              <div style={{ flex: '1', minWidth: '0', display: 'flex', flexDirection: 'column' }}>
                <b>{a.title}</b>
                {a.body && <span className="sm" style={{ color: 'var(--muted)', fontSize: '14px' }}>{a.body}</span>}
              </div>
              <span className="mono desk" style={{ width: '120px' }}>{when(a.at)}</span>
              {rowLink(a)}
              {!a.read && <button type="button" className="btn s b-line" onClick={() => markOne(a)}>Mark read</button>}
            </div>
          ))}
          {list.length > SHOW_MAX && (
            <p className="sm" style={{ margin: 0, padding: '12px 20px', borderTop: '1px solid var(--line)', color: 'var(--faint)' }}>
              Showing the newest {SHOW_MAX} of {list.length}.
            </p>
          )}
        </section>
        )}
        {editing && <EditBusModal bus={editing.bus} focus={editing.focus} onClose={() => setEditing(null)} />}
        {correcting && <RefillModal f={f} bus={correcting.bus} alert={correcting.alert} onClose={() => setCorrecting(null)} />}
        {maint && <MaintModal f={f} mode={maint.mode} busId={maint.busId} focus={maint.focus} onClose={() => setMaint(null)} />}
      </div>
    </PageBody>
  );
};

export default Alerts;
